import { Fragment, ReactNode, JSX } from 'react'
import { isEmpty } from 'ramda'

import { Table, TdProps, ThProps, TrProps } from 'app/common/ui-components/table'

import { CommonRenderHeaderRow, defaultRenderHeaderRow } from './default-render-header-row'
import { CommonRenderHeaderCellProps, defaultRenderHeaderCell } from './default-render-header-cell'
import { CommonRenderRow, defaultRenderRow } from './default-render-row'
import { CommonRenderCell, defaultRenderCell } from './default-render-cell'
import { SkeletonLoadingTBody, StatusMessage } from '../../page-template'
import { TheadWithSticky } from './thead-with-sticky'

type Props<T extends Record<string, unknown>, K extends keyof T & string> = {
  className?: string
  data: Array<T>
  dataArrange: Array<K>
  cellProps?: Record<K, Partial<TdProps>>
  rowProps?: Partial<TrProps>
  headerData: Array<Record<K, unknown>>
  headerCellProps?: Record<K, Partial<ThProps>>
  headerRowProps?: Partial<TrProps>
  renderRow?: CommonRenderRow<T, K>
  renderCell?: CommonRenderCell<T, K>
  renderHeaderRow?: CommonRenderHeaderRow<K>
  renderHeaderCell?: <TH extends Record<K, unknown>, KK extends keyof TH & string>(
    props: CommonRenderHeaderCellProps<TH, KK>,
  ) => ReactNode
  onRowClick?: (rowData: T) => void
  loading?: boolean
  placeholder?: string
  stickyHeader?: boolean
  settings?: Record<string, boolean>
  updateKey?: Array<unknown>
  classNameTr?: string
  renderStub?: () => JSX.Element
  resetStickyShift?: boolean
  skeletonRows?: number
}

export const CommonTableTemplate: {
  <T extends Record<string, unknown>, K extends keyof T & string>(props: Props<T, K>): JSX.Element
  defaultRenderHeaderRow: typeof defaultRenderHeaderRow
  defaultRenderHeaderCell: typeof defaultRenderHeaderCell
  defaultRenderRow: typeof defaultRenderRow
  defaultRenderCell: typeof defaultRenderCell
} = ({
  className,
  data,
  dataArrange,
  cellProps,
  rowProps,
  headerData,
  headerCellProps,
  headerRowProps,
  renderRow = defaultRenderRow,
  renderCell = defaultRenderCell,
  renderHeaderRow = defaultRenderHeaderRow,
  renderHeaderCell = defaultRenderHeaderCell,
  onRowClick,
  loading,
  placeholder,
  stickyHeader,
  settings,
  updateKey,
  classNameTr,
  renderStub,
  resetStickyShift,
  skeletonRows,
}) => {
  const empty = isEmpty(data)

  if (renderStub && empty && !loading) {
    return renderStub()
  }

  return (
    <Table className={className} wide>
      {stickyHeader ? (
        <TheadWithSticky updateKey={updateKey} resetShift={resetStickyShift}>
          {(stuck, shadow) =>
            headerData.map((headerRowData, headerRowIndex) => (
              <Fragment key={headerRowIndex}>
                {renderHeaderRow({
                  headerRowData,
                  dataArrange,
                  headerRowIndex,
                  headerRowsCount: headerData.length,
                  renderHeaderCell,
                  headerCellProps,
                  headerRowProps,
                  stuck,
                  shadow,
                  settings,
                })}
              </Fragment>
            ))
          }
        </TheadWithSticky>
      ) : (
        <thead>
          {headerData.map((headerRowData, headerRowIndex) => (
            <Fragment key={headerRowIndex}>
              {renderHeaderRow({
                headerRowData,
                dataArrange,
                headerRowIndex,
                headerRowsCount: headerData.length,
                renderHeaderCell,
                headerCellProps,
                settings,
              })}
            </Fragment>
          ))}
        </thead>
      )}

      <tbody>
        {loading && (
          <SkeletonLoadingTBody
            rows={skeletonRows || data.length || 10}
            columns={dataArrange.length}
            itemWidth="random"
            onlyRows
          />
        )}

        {!loading &&
          data.map((rowData, rowIndex) => (
            <Fragment
              key={
                ('id' in rowData ? rowData.id : 'ID' in rowData ? rowData.ID : -rowIndex) as
                  | string
                  | number
              }
            >
              {renderRow({
                rowData,
                dataArrange,
                rowIndex,
                renderCell,
                onRowClick,
                cellProps,
                rowProps,
                settings,
                className: classNameTr,
              })}
            </Fragment>
          ))}

        {!loading && empty && (
          <StatusMessage asTableRow colSpan={dataArrange.length}>
            {placeholder || 'Нет данных, удовлетворяющих заданным условиям'}
          </StatusMessage>
        )}
      </tbody>
    </Table>
  )
}

CommonTableTemplate.defaultRenderHeaderRow = defaultRenderHeaderRow
CommonTableTemplate.defaultRenderHeaderCell = defaultRenderHeaderCell
CommonTableTemplate.defaultRenderRow = defaultRenderRow
CommonTableTemplate.defaultRenderCell = defaultRenderCell
